import { useState, useRef, useEffect, memo, useCallback } from 'react'
import { useStore } from '../store/useStore'
import { ChevronUp, ChevronDown, FolderOpen, List, Plus, Check, X, GripVertical, Users, Eye, EyeOff, Pencil, Menu } from 'lucide-react'
import ConfirmDialog from './ConfirmDialog'
import { NotificationBell } from './NotificationBell'
import AppMenuDropdown from './AppMenuDropdown'
import { TEAMS_ENABLED } from '../config/features'
import ContactForm from './ContactForm'
import { useTranslation } from 'react-i18next'
import { ShareProjectModal } from './team/ShareProjectModal'
import { supabase } from '../lib/supabase'
import { getUserColorFromId, getUserInitial } from '../utils/userColor'
import { getTeamMembersWithProfiles } from '../services/teamService'
import { useAuth } from '../contexts/AuthContext'
import ProjectSettingsModal, { type Milestone, type MilestoneTemplate } from './ProjectSettingsModal'
import MilestoneTemplateModal from './MilestoneTemplateModal'
import {
  getMilestonesByProjectId,
  getMilestoneTemplatesByTeamId,
  getMilestoneTemplatesByUserId,
  createProjectMilestone,
  updateProjectMilestone,
  deleteProjectMilestone,
  createMilestoneTemplate,
  createPersonalMilestoneTemplate,
  updateMilestoneTemplate,
  deleteMilestoneTemplate,
  initializePersonalTemplatesIfNeeded,
  initializeTeamTemplatesIfNeeded,
  type ProjectMilestone,
  type MilestoneTemplate as DBMilestoneTemplate,
} from '../services/milestoneService'

// DB型からフロントエンド型への変換
const convertDBMilestoneToLocal = (dbMilestone: ProjectMilestone): Milestone => ({
  id: dbMilestone.id,
  projectId: dbMilestone.project_id,
  templateId: dbMilestone.template_id,
  title: dbMilestone.title,
  startDate: dbMilestone.start_date,
  endDate: dbMilestone.end_date,
  iconType: dbMilestone.icon_type,
  iconValue: dbMilestone.icon_value,
  color: dbMilestone.color,
  sortOrder: dbMilestone.sort_order,
})

const convertDBTemplateToLocal = (dbTemplate: DBMilestoneTemplate): MilestoneTemplate => ({
  id: dbTemplate.id,
  teamId: dbTemplate.team_id || '',
  title: dbTemplate.title,
  iconType: dbTemplate.icon_type,
  iconValue: dbTemplate.icon_value,
  color: dbTemplate.color,
  sortOrder: dbTemplate.sort_order,
})
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  defaultAnimateLayoutChanges
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// チームメンバーリスト表示コンポーネント（モバイル用）
function TeamMembersPopover({ teamId, memberCount, onClose }: { teamId: string, memberCount: number, onClose: () => void }) {
  const [members, setMembers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const loadMembers = async () => {
      try {
        // RPC関数でチームメンバー一覧を取得（プロファイル付き）
        const membersData = await getTeamMembersWithProfiles(teamId)

        // データ形式を既存のコードに合わせて変換
        const membersWithProfiles = membersData.map(member => ({
          id: member.id,
          role: member.role,
          joined_at: member.joined_at,
          user_id: member.user_id,
          user_profiles: {
            id: member.user_id,
            nickname: member.nickname,
            email: member.email
          }
        }))

        setMembers(membersWithProfiles)
      } catch (error) {
        console.error('Error loading team members:', error)
      } finally {
        setLoading(false)
      }
    }

    loadMembers()
  }, [teamId])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return '👑'
      case 'admin': return '🛡️'
      default: return '👤'
    }
  }

  const getRoleName = (role: string) => {
    switch (role) {
      case 'owner': return 'オーナー'
      case 'admin': return '管理者'
      default: return 'メンバー'
    }
  }

  return (
    <div
      ref={popoverRef}
      className="absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3 z-50 min-w-[220px] max-w-[280px]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
        <Users className="h-3.5 w-3.5" />
        チームメンバー ({memberCount}名)
      </div>
      {loading ? (
        <div className="text-xs text-gray-500 dark:text-gray-400 py-2">読み込み中...</div>
      ) : (
        <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
          {members.map((member) => {
            const userProfile = member.user_profiles
            const colorClasses = userProfile ? getUserColorFromId(userProfile.id) : 'from-gray-400 to-gray-600'
            const initial = userProfile ? getUserInitial(userProfile.nickname, userProfile.email) : '??'

            return (
              <div key={member.id} className="flex items-center gap-2 text-xs p-2 rounded bg-gray-50 dark:bg-gray-700">
                {/* ユーザーアイコン */}
                <div
                  className={`h-6 w-6 rounded-full bg-gradient-to-br ${colorClasses} flex items-center justify-center flex-shrink-0`}
                  title={userProfile?.nickname || userProfile?.email || '不明'}
                >
                  <span className="text-white font-semibold text-[10px]">
                    {initial}
                  </span>
                </div>
                {/* 権限アイコン */}
                <span className="text-sm">{getRoleIcon(member.role)}</span>
                {/* ユーザー情報 */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 dark:text-white truncate">
                    {userProfile?.nickname || userProfile?.email || '不明'}
                  </div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400">
                    {getRoleName(member.role)}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

interface SortableProjectProps {
  project: any
  isSelected: boolean
  isEditing: boolean
  editProjectName: string
  onSelect: () => void
  onCancelEdit: () => void
  onSaveEdit: () => void
  setEditProjectName: (name: string) => void
  updateProject: (id: string, updates: any) => Promise<void>
  deleteProject: (id: string) => Promise<void>
  isTemporary?: boolean
  onOpenSettings?: () => void
}

const SortableProject = memo(function SortableProject({
  project,
  isSelected,
  isEditing,
  editProjectName,
  onSelect,
  onCancelEdit,
  onSaveEdit,
  setEditProjectName,
  updateProject,
  deleteProject,
  isTemporary = false,
  onOpenSettings
}: SortableProjectProps) {
  const [showMembersList, setShowMembersList] = useState(false)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // 現在のユーザーIDを取得
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      setCurrentUserId(user?.id || null)
    }
    getCurrentUser()
  }, [])

  // プロジェクトオーナーかどうかを判定
  const isProjectOwner = currentUserId === project.user_id

  // ドラッグ中は他の要素がすぐに反応するようにアニメーションを調整
  const animateLayoutChanges = (args: Parameters<typeof defaultAnimateLayoutChanges>[0]) => {
    const { isSorting, wasDragging } = args
    if (isSorting || wasDragging) {
      return defaultAnimateLayoutChanges(args)
    }
    return true // 常にアニメーション
  }

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: project.id,
    disabled: isTemporary,  // 一時保存プロジェクトはドラッグ無効
    animateLayoutChanges
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition, // ドラッグ中はトランジションなし（遅延なく追従）
    opacity: isDragging ? 1 : 1,
    zIndex: isDragging ? 9999 : 1,
    position: 'relative' as const,
    boxShadow: isDragging ? '0 15px 35px rgba(0,0,0,0.35)' : 'none',
    scale: isDragging ? '1.03' : '1',
    backgroundColor: isDragging ? 'white' : undefined
  } as React.CSSProperties

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 p-2 bg-white rounded-lg shadow-sm">
        <input
          type="text"
          value={editProjectName}
          onChange={(e) => setEditProjectName(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && onSaveEdit()}
          className="flex-1 px-2 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          autoFocus
        />
        <button
          onClick={onSaveEdit}
          className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          onClick={onCancelEdit}
          className="p-1.5 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    )
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-project-id={project.id}
      className={`flex items-center gap-2 p-2.5 rounded-lg transition-all border-b border-gray-400 dark:border-gray-600 ${
        isSelected
          ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md'
          : 'bg-white text-gray-700 hover:bg-indigo-50 shadow-sm'
      }`}
    >
      {!isTemporary && (
        <div
          {...attributes}
          {...listeners}
          className="touch-none flex-shrink-0 cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4 text-gray-400" />
        </div>
      )}
      {isTemporary && (
        <div className="flex-shrink-0 w-4">
          {/* 一時保存プロジェクトは移動不可（スペース確保） */}
        </div>
      )}
      <div className="flex-1 flex flex-col gap-2 min-w-0">
        {/* 1行目: プロジェクト名と色 */}
        <button
          onClick={onSelect}
          className="flex items-center gap-2 text-left"
        >
          <div
            className="w-4 h-4 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
            style={{ backgroundColor: project.color }}
          />
          <span className="font-medium text-sm truncate">{project.name}</span>
        </button>

        {/* 2行目: 幅いっぱいのバー（チーム情報 or 説明文 + アクションボタン） */}
        <div className={`flex items-center gap-2 w-full px-3 py-0.5 rounded-lg ${
          isSelected
            ? 'bg-white bg-opacity-15'
            : project.team
              ? 'bg-indigo-50'
              : 'bg-gray-100'
        }`}>
          {/* 左側: チーム情報 or プロジェクト説明 */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {project.team ? (
              <>
                {/* ロールアイコン */}
                {project.team_role && (
                  <span className="text-base flex-shrink-0">
                    {project.team_role === 'owner' ? '👑' : project.team_role === 'admin' ? '🛡️' : '👤'}
                  </span>
                )}
                {/* チーム名 - 固定幅 */}
                <span className={`text-[11px] px-2 py-1 rounded-full font-medium w-[60px] truncate inline-block text-center ${
                  isSelected ? 'bg-white bg-opacity-25 text-white' : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-800 dark:text-indigo-200'
                }`} title={project.team.name}>
                  {project.team.name}
                </span>
                {/* メンバー数 */}
                {project.team_member_count !== undefined && (
                  <span className="relative flex-shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowMembersList(!showMembersList)
                      }}
                      className={`text-[11px] px-2 py-1 rounded-full font-medium flex items-center gap-1 hover:opacity-80 transition-opacity ${
                        isSelected ? 'bg-white bg-opacity-25 text-white' : 'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-200'
                      }`}
                    >
                      <Users className="h-3 w-3" />
                      {project.team_member_count}
                    </button>
                    {showMembersList && project.team_id && (
                      <TeamMembersPopover
                        teamId={project.team_id}
                        memberCount={project.team_member_count}
                        onClose={() => setShowMembersList(false)}
                      />
                    )}
                  </span>
                )}
              </>
            ) : (
              project.description && (
                <div className={`text-[11px] truncate ${
                  isSelected ? 'text-white text-opacity-90' : 'text-gray-600 dark:text-gray-300'
                }`}>
                  {project.name === '一時保存' ? 'AI音声機能で作成した未分類タスク' : project.description}
                </div>
              )
            )}
          </div>

          {/* 右側: アクションボタン（目玉と鉛筆のみ） */}
          {!isTemporary && (
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* 表示/非表示トグル（目玉アイコン） */}
              {project.is_team_project ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    const currentValue = project.user_show_in_all_projects !== undefined ? project.user_show_in_all_projects : true
                    updateProject(project.id, { show_in_all_projects: !currentValue })
                  }}
                  className="p-1.5 rounded hover:bg-white hover:bg-opacity-20 transition-colors touch-manipulation"
                  title={(project.user_show_in_all_projects !== undefined ? project.user_show_in_all_projects : true) ? "全プロジェクトで表示中" : "全プロジェクトで非表示"}
                >
                  {(project.user_show_in_all_projects !== undefined ? project.user_show_in_all_projects : true) ? (
                    <Eye className="h-5 w-5 text-gray-700" />
                  ) : (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    updateProject(project.id, { show_in_all_projects: !project.show_in_all_projects })
                  }}
                  className="p-1.5 rounded hover:bg-white hover:bg-opacity-20 transition-colors touch-manipulation"
                  title={project.show_in_all_projects ? "全プロジェクトで表示中" : "全プロジェクトで非表示"}
                >
                  {project.show_in_all_projects ? (
                    <Eye className="h-5 w-5 text-gray-700" />
                  ) : (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              )}
              {/* 設定（鉛筆アイコン） - オーナーのみ */}
              {isProjectOwner && onOpenSettings && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenSettings()
                  }}
                  className="p-1.5 rounded hover:bg-white hover:bg-opacity-20 transition-colors touch-manipulation"
                  title="プロジェクト設定"
                >
                  <Pencil className="h-5 w-5 text-gray-700" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="プロジェクトを削除"
        message={`「${project.name}」を削除しますか？\n関連するToDoも削除されます。`}
        confirmText="削除"
        cancelText="キャンセル"
        onConfirm={() => {
          deleteProject(project.id)
          setShowDeleteConfirm(false)
        }}
        onCancel={() => setShowDeleteConfirm(false)}
        variant="danger"
      />
    </div>
  )
})

interface BottomProjectSelectorProps {
  /** 設定モーダルを開く（旧ヘッダー機能の集約用） */
  onOpenSettings?: () => void
}

export default function BottomProjectSelector({ onOpenSettings }: BottomProjectSelectorProps = {}) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { projects, selectedProjectId, setSelectedProjectId, addProject, updateProject, updateProjectOrder, deleteProject, setIsProjectSelectorOpen, isPersonalSectionVisible, isTeamSectionVisible, setIsPersonalSectionVisible, setIsTeamSectionVisible } = useStore()
  const [isOpen, setIsOpen] = useState(false) // 初期状態で閉じる
  const [isAddingProject, setIsAddingProject] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editProjectName, setEditProjectName] = useState('')
  const [sharingProjectId, setSharingProjectId] = useState<string | null>(null)

  // 旧ヘッダー機能の集約（メニュー・お問い合わせ）用state
  const [isAppMenuOpen, setIsAppMenuOpen] = useState(false)
  const [showContactForm, setShowContactForm] = useState(false)
  const appMenuRef = useRef<HTMLDivElement>(null)

  // メニュー外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (appMenuRef.current && !appMenuRef.current.contains(event.target as Node)) {
        setIsAppMenuOpen(false)
      }
    }
    if (isAppMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isAppMenuOpen])

  // プロジェクト設定モーダル用state
  const [settingsProjectId, setSettingsProjectId] = useState<string | null>(null)
  const [localMilestones, setLocalMilestones] = useState<Milestone[]>([])
  const [localMilestoneTemplates, setLocalMilestoneTemplates] = useState<MilestoneTemplate[]>([])
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false)

  // 設定モーダルが開いたときにDBからマイルストーンを取得
  useEffect(() => {
    if (!settingsProjectId) {
      setLocalMilestones([])
      return
    }

    const fetchMilestones = async () => {
      try {
        const dbMilestones = await getMilestonesByProjectId(settingsProjectId)
        setLocalMilestones(dbMilestones.map(convertDBMilestoneToLocal))
      } catch (error) {
        console.error('Failed to fetch milestones:', error)
      }
    }
    fetchMilestones()
  }, [settingsProjectId])

  // 設定モーダルが開いたときにDBからテンプレートを取得
  useEffect(() => {
    const project = projects.find(p => p.id === settingsProjectId)
    const teamId = project?.team_id

    const fetchTemplates = async () => {
      try {
        if (teamId) {
          await initializeTeamTemplatesIfNeeded(teamId)
          const dbTemplates = await getMilestoneTemplatesByTeamId(teamId)
          setLocalMilestoneTemplates(dbTemplates.map(convertDBTemplateToLocal))
        } else if (user?.id) {
          await initializePersonalTemplatesIfNeeded(user.id)
          const dbTemplates = await getMilestoneTemplatesByUserId(user.id)
          setLocalMilestoneTemplates(dbTemplates.map(convertDBTemplateToLocal))
        } else {
          setLocalMilestoneTemplates([])
        }
      } catch (error) {
        console.error('Failed to fetch templates:', error)
      }
    }

    if (settingsProjectId) {
      fetchTemplates()
    } else {
      setLocalMilestoneTemplates([])
    }
  }, [settingsProjectId, projects, user?.id])

  const settingsProject = projects.find(p => p.id === settingsProjectId) || null

  // 現在のプロジェクトのチームIDを取得するヘルパー
  const getCurrentTeamId = useCallback(() => {
    const project = projects.find(p => p.id === settingsProjectId)
    return project?.team_id || null
  }, [projects, settingsProjectId])

  // マイルストーンハンドラー
  const handleAddMilestone = useCallback(async (milestone: Partial<Milestone>) => {
    if (!settingsProjectId) return
    try {
      const created = await createProjectMilestone({
        project_id: settingsProjectId,
        template_id: milestone.templateId || null,
        title: milestone.title || '',
        start_date: milestone.startDate || new Date().toISOString().split('T')[0],
        end_date: milestone.endDate || null,
        icon_type: milestone.iconType || 'emoji',
        icon_value: milestone.iconValue || '📋',
        color: milestone.color || '#6366f1',
        sort_order: milestone.sortOrder || localMilestones.length,
      })
      setLocalMilestones(prev => [...prev, convertDBMilestoneToLocal(created)])
    } catch (error) {
      console.error('Failed to create milestone:', error)
    }
  }, [settingsProjectId, localMilestones.length])

  const handleUpdateMilestone = useCallback(async (milestoneId: string, updates: Partial<Milestone>) => {
    try {
      const updated = await updateProjectMilestone(milestoneId, {
        title: updates.title,
        start_date: updates.startDate,
        end_date: updates.endDate,
        icon_type: updates.iconType,
        icon_value: updates.iconValue,
        color: updates.color,
        sort_order: updates.sortOrder,
      })
      setLocalMilestones(prev =>
        prev.map(m => m.id === milestoneId ? convertDBMilestoneToLocal(updated) : m)
      )
    } catch (error) {
      console.error('Failed to update milestone:', error)
    }
  }, [])

  const handleDeleteMilestone = useCallback(async (milestoneId: string) => {
    try {
      await deleteProjectMilestone(milestoneId)
      setLocalMilestones(prev => prev.filter(m => m.id !== milestoneId))
    } catch (error) {
      console.error('Failed to delete milestone:', error)
    }
  }, [])

  // テンプレートハンドラー
  const handleAddTemplate = useCallback(async (template: Partial<MilestoneTemplate>) => {
    const teamId = getCurrentTeamId()
    try {
      let created: DBMilestoneTemplate
      if (teamId) {
        created = await createMilestoneTemplate({
          team_id: teamId,
          title: template.title || '',
          icon_type: template.iconType || 'emoji',
          icon_value: template.iconValue || '📋',
          color: template.color || '#6366f1',
          sort_order: template.sortOrder || localMilestoneTemplates.length,
        })
      } else if (user?.id) {
        created = await createPersonalMilestoneTemplate(user.id, {
          title: template.title || '',
          icon_type: template.iconType || 'emoji',
          icon_value: template.iconValue || '📋',
          color: template.color || '#6366f1',
          sort_order: template.sortOrder || localMilestoneTemplates.length,
        })
      } else {
        console.error('No team ID or user ID available for creating template')
        return
      }
      setLocalMilestoneTemplates(prev => [...prev, convertDBTemplateToLocal(created)])
    } catch (error) {
      console.error('Failed to create template:', error)
    }
  }, [getCurrentTeamId, localMilestoneTemplates.length, user?.id])

  const handleUpdateTemplate = useCallback(async (templateId: string, updates: Partial<MilestoneTemplate>) => {
    try {
      const updated = await updateMilestoneTemplate(templateId, {
        title: updates.title,
        icon_type: updates.iconType,
        icon_value: updates.iconValue,
        color: updates.color,
        sort_order: updates.sortOrder,
      })
      setLocalMilestoneTemplates(prev =>
        prev.map(t => t.id === templateId ? convertDBTemplateToLocal(updated) : t)
      )
    } catch (error) {
      console.error('Failed to update template:', error)
    }
  }, [])

  const handleDeleteTemplate = useCallback(async (templateId: string) => {
    try {
      await deleteMilestoneTemplate(templateId)
      setLocalMilestoneTemplates(prev => prev.filter(t => t.id !== templateId))
    } catch (error) {
      console.error('Failed to delete template:', error)
    }
  }, [])

  // isOpenをラップしてグローバル状態も更新
  const toggleProjectSelector = (open: boolean) => {
    setIsOpen(open)
    setIsProjectSelectorOpen(open)
  }

  // ツアーからのイベントを受け取る
  useEffect(() => {
    const handleTourAction = (event: CustomEvent<{ action: 'open' | 'close' }>) => {
      if (event.detail.action === 'open') {
        toggleProjectSelector(true)
      } else if (event.detail.action === 'close') {
        toggleProjectSelector(false)
      }
    }

    window.addEventListener('tour-action', handleTourAction as EventListener)
    return () => {
      window.removeEventListener('tour-action', handleTourAction as EventListener)
    }
  }, [])

  // プロジェクトセレクターが開いたときにツアーを次へ進める
  useEffect(() => {
    if (isOpen) {
      // ツアーに「プロジェクトセレクターが開いた」ことを通知
      window.dispatchEvent(new CustomEvent('project-selector-opened'))
    }
  }, [isOpen])

  // プロジェクトを一時保存、個人プロジェクト、チームプロジェクトに分離
  const temporaryProject = projects.find(p => p.name === '一時保存')
  const personalProjects = projects.filter(p => p.name !== '一時保存' && !p.is_team_project)
  const teamProjects = projects.filter(p => p.name !== '一時保存' && p.is_team_project)
  const regularProjects = projects.filter(p => p.name !== '一時保存') // ドラッグ&ドロップ用

  // Safari対策：TouchSensorとMouseSensorを分けて使用（TodoListと同じ設定）
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,  // 少し長めのdelayでタップとドラッグを区別
        tolerance: 10,  // 小さめのtoleranceで精度を上げる
      },
    })
  )

  const projectColors = [
    // Row 1: 青〜紫系
    '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#C026D3',
    // Row 2: ピンク〜赤系
    '#EC4899', '#F43F5E', '#EF4444', '#DC2626', '#B91C1C',
    // Row 3: オレンジ〜黄系
    '#F97316', '#EA580C', '#EAB308', '#FACC15', '#FDE047',
    // Row 4: 緑〜シアン系
    '#84CC16', '#22C55E', '#10B981', '#14B8A6', '#06B6D4'
  ]

  const selectedProject = projects.find(p => p.id === selectedProjectId)
  const displayName = selectedProject ? selectedProject.name : t('project.allProjects')

  const handleProjectSelect = (projectId: string | null) => {
    // モーダルを即座に閉じる（UIの反応速度優先）
    toggleProjectSelector(false)
    // プロジェクト選択は非同期で実行（ユーザーを待たせない）
    setSelectedProjectId(projectId)
  }

  const handleAddProject = async () => {
    if (!newProjectName.trim()) return

    const randomColor = projectColors[Math.floor(Math.random() * projectColors.length)]

    // UIをすぐに閉じる（ユーザー体験優先）
    setNewProjectName('')
    setIsAddingProject(false)
    toggleProjectSelector(false)

    // プロジェクト作成をバックグラウンドで実行
    const newProject = await addProject({
      name: newProjectName.trim(),
      description: null,
      color: randomColor,
      is_active: true,
      show_thumbnail: false,
      show_in_all_projects: true,
      is_team_project: false,
      team_id: null
    })

    if (newProject) {
      // プロジェクト選択も非同期で実行
      setSelectedProjectId(newProject.id)
    }
  }

  const handleUpdateProject = async (id: string) => {
    if (editProjectName.trim()) {
      await updateProject(id, { name: editProjectName.trim() })
      setEditingId(null)
      setEditProjectName('')
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    // ドラッグ元のプロジェクトを特定
    const draggedProject = regularProjects.find(p => p.id === active.id)
    if (!draggedProject) return

    // 個人プロジェクトかチームプロジェクトかを判定
    const isPersonalProject = !draggedProject.is_team_project
    const targetProjects = isPersonalProject ? personalProjects : teamProjects

    const oldIndex = targetProjects.findIndex(p => p.id === active.id)
    const newIndex = targetProjects.findIndex(p => p.id === over.id)

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      // display_orderの重複チェック
      const orderSet = new Set<number>()
      let hasDuplicates = false
      for (const project of targetProjects) {
        if (orderSet.has(project.display_order)) {
          hasDuplicates = true
          break
        }
        orderSet.add(project.display_order)
      }

      if (hasDuplicates) {
        // 重複がある場合: 全プロジェクトの順序を再割り当て
        const reorderedProjects = [...targetProjects]
        const [movedItem] = reorderedProjects.splice(oldIndex, 1)
        reorderedProjects.splice(newIndex, 0, movedItem)

        // 新しい順序を1000刻みで割り当て
        for (let i = 0; i < reorderedProjects.length; i++) {
          await updateProjectOrder(reorderedProjects[i].id, i * 1000)
        }
      } else {
        // 重複がない場合: Fractional Indexing
        let newOrder: number

        if (oldIndex < newIndex) {
          // 下に移動: over要素とその次の要素の間に配置
          const targetOrder = targetProjects[newIndex]?.display_order ?? 0
          const nextOrder = targetProjects[newIndex + 1]?.display_order ?? targetOrder + 2000
          newOrder = Math.floor((targetOrder + nextOrder) / 2)
        } else {
          // 上に移動: over要素の前の要素とover要素の間に配置
          const targetOrder = targetProjects[newIndex]?.display_order ?? 0
          const prevOrder = targetProjects[newIndex - 1]?.display_order ?? targetOrder - 2000
          newOrder = Math.floor((prevOrder + targetOrder) / 2)
        }

        await updateProjectOrder(active.id as string, newOrder)
      }
    }
  }

  return (
    <div className="fixed left-0 right-0 bottom-0 z-40 lg:hidden">
      {/* プロジェクト一覧 */}
      {isOpen && (
        <>
          {/* 背景オーバーレイ */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 -z-10"
            onClick={() => toggleProjectSelector(false)}
          />

          {/* プロジェクト一覧カード */}
          <div className="bg-gradient-to-b from-indigo-100 via-purple-100 to-indigo-50 border-t-2 border-indigo-500 shadow-2xl max-h-[70vh] overflow-y-auto animate-slide-up momentum-scroll" data-tour="bottom-project-list">
            <div className="p-4 space-y-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">{t('project.selectProject')}</h3>
                <button
                  onClick={() => setIsAddingProject(!isAddingProject)}
                  className="p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors tap-highlight"
                  title="新しいプロジェクトを追加"
                  data-tour="bottom-add-project"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              {/* プロジェクト追加フォーム */}
              {isAddingProject && (
                <div className="flex items-center gap-2 p-2 bg-white rounded-lg mb-2 shadow-sm">
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddProject()}
                    placeholder="新しいプロジェクト名"
                    className="flex-1 px-2 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    autoFocus
                  />
                  <button
                    onClick={handleAddProject}
                    className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setIsAddingProject(false)
                      setNewProjectName('')
                    }}
                    className="p-1.5 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* すべてのプロジェクト（一番上）- 常に薄い色で表示 */}
              <button
                onClick={() => handleProjectSelect(null)}
                className={`w-full flex items-center gap-2.5 p-2.5 rounded-lg transition-all ${
                  !selectedProjectId
                    ? 'bg-gradient-to-r from-pink-200/50 to-rose-200/50 text-gray-700 shadow-md border-2 border-pink-500'
                    : 'bg-gradient-to-r from-pink-100/40 to-rose-100/40 text-gray-600 hover:from-pink-200/45 hover:to-rose-200/45 shadow-sm border border-pink-400'
                }`}
              >
                <List className="h-4 w-4 flex-shrink-0" />
                <div className="flex-1 text-left">
                  <div className="font-medium text-sm">{t('project.allProjects')}</div>
                </div>
              </button>

              {/* 一時保存プロジェクト（固定位置・2番目） */}
              {temporaryProject && (
                <SortableProject
                  key={temporaryProject.id}
                  project={temporaryProject}
                  isSelected={selectedProjectId === temporaryProject.id}
                  isEditing={editingId === temporaryProject.id}
                  editProjectName={editProjectName}
                  onSelect={() => handleProjectSelect(temporaryProject.id)}
                  onCancelEdit={() => {
                    setEditingId(null)
                    setEditProjectName('')
                  }}
                  onSaveEdit={() => handleUpdateProject(temporaryProject.id)}
                  setEditProjectName={setEditProjectName}
                  updateProject={updateProject}
                  deleteProject={deleteProject}
                  isTemporary={true}
                />
              )}

              {/* 個人プロジェクト（並び替え可能） */}
              {personalProjects.length > 0 && (
                <div>
                  <div className="mt-3 px-4 py-2.5 text-xs font-bold text-gray-700 bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300 flex items-center gap-2">
                    <div className="w-1 h-4 bg-gray-600 rounded-full"></div>
                    個人プロジェクト
                    <span className="text-[10px] text-gray-500 font-medium px-1.5 py-0.5 bg-white rounded-full">
                      {personalProjects.length}件
                    </span>
                    {/* セクション表示/非表示トグル */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsPersonalSectionVisible(!isPersonalSectionVisible)
                      }}
                      className={`ml-auto p-1 rounded transition-colors ${
                        isPersonalSectionVisible
                          ? 'text-gray-600 hover:bg-gray-200'
                          : 'text-gray-400 hover:bg-gray-200'
                      }`}
                      title={isPersonalSectionVisible ? "「全プロジェクト」表示時に非表示にする" : "「全プロジェクト」表示時に表示する"}
                    >
                      {isPersonalSectionVisible ? (
                        <Eye className="h-4 w-4" />
                      ) : (
                        <EyeOff className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={personalProjects.map(p => p.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {personalProjects.map((project) => (
                        <SortableProject
                          key={project.id}
                          project={project}
                          isSelected={selectedProjectId === project.id}
                          isEditing={editingId === project.id}
                          editProjectName={editProjectName}
                          onSelect={() => handleProjectSelect(project.id)}
                          onCancelEdit={() => {
                            setEditingId(null)
                            setEditProjectName('')
                          }}
                          onSaveEdit={() => handleUpdateProject(project.id)}
                          setEditProjectName={setEditProjectName}
                          updateProject={updateProject}
                          deleteProject={deleteProject}
                          isTemporary={false}
                          onOpenSettings={() => setSettingsProjectId(project.id)}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {/* チームプロジェクト（並び替え可能） - 個人プロジェクトとの間隔を拡大 */}
              {teamProjects.length > 0 && (
                <div style={{ marginTop: '80px' }}>
                  <div className="px-4 py-2.5 text-xs font-bold text-indigo-700 bg-gradient-to-r from-indigo-100 to-blue-50 border-b-2 border-indigo-300 flex items-center gap-2">
                    <div className="w-1 h-4 bg-indigo-600 rounded-full"></div>
                    <Users className="h-3.5 w-3.5" />
                    {TEAMS_ENABLED ? 'チームプロジェクト' : '共有プロジェクト'}
                    <span className="text-[10px] text-indigo-500 font-medium px-1.5 py-0.5 bg-white rounded-full">
                      {teamProjects.length}件
                    </span>
                    {/* セクション表示/非表示トグル */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsTeamSectionVisible(!isTeamSectionVisible)
                      }}
                      className={`ml-auto p-1 rounded transition-colors ${
                        isTeamSectionVisible
                          ? 'text-indigo-600 hover:bg-indigo-200'
                          : 'text-indigo-400 hover:bg-indigo-200'
                      }`}
                      title={isTeamSectionVisible ? "「全プロジェクト」表示時に非表示にする" : "「全プロジェクト」表示時に表示する"}
                    >
                      {isTeamSectionVisible ? (
                        <Eye className="h-4 w-4" />
                      ) : (
                        <EyeOff className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={teamProjects.map(p => p.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {teamProjects.map((project) => (
                        <SortableProject
                          key={project.id}
                          project={project}
                          isSelected={selectedProjectId === project.id}
                          isEditing={editingId === project.id}
                          editProjectName={editProjectName}
                          onSelect={() => handleProjectSelect(project.id)}
                          onCancelEdit={() => {
                            setEditingId(null)
                            setEditProjectName('')
                          }}
                          onSaveEdit={() => handleUpdateProject(project.id)}
                          setEditProjectName={setEditProjectName}
                          updateProject={updateProject}
                          deleteProject={deleteProject}
                          isTemporary={false}
                          onOpenSettings={() => setSettingsProjectId(project.id)}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* 選択中のプロジェクト表示（常に表示）+ 右側に通知・メニュー（旧ヘッダー機能の集約） */}
      <div
        className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg"
        data-tour="bottom-project-selector"
      >
        <div className="flex items-center">
          <button
            onClick={() => toggleProjectSelector(!isOpen)}
            className="flex-1 min-w-0 pt-3 pb-4 pl-4 pr-2 flex items-center gap-2.5 tap-highlight"
          >
            {selectedProject ? (
              <FolderOpen className="h-4 w-4 flex-shrink-0" />
            ) : (
              <List className="h-4 w-4 flex-shrink-0" />
            )}
            <span className="font-medium text-sm truncate">{displayName}</span>
            {isOpen ? (
              <ChevronDown className="h-4 w-4 flex-shrink-0" />
            ) : (
              <ChevronUp className="h-4 w-4 flex-shrink-0" />
            )}
          </button>

          {/* 右側: 通知ベル + メニュー */}
          <div className="flex items-center gap-0.5 pr-2 pt-2 pb-3 flex-shrink-0">
            <NotificationBell
              direction="up"
              buttonClassName="relative p-2 text-white hover:bg-white/20 rounded-full transition-colors"
            />
            <div className="relative" ref={appMenuRef}>
              <button
                onClick={() => setIsAppMenuOpen(!isAppMenuOpen)}
                className="p-2 text-white hover:bg-white/20 rounded-full transition-colors tap-highlight"
                aria-label="メニュー"
              >
                {isAppMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              {isAppMenuOpen && (
                <AppMenuDropdown
                  direction="up"
                  onClose={() => setIsAppMenuOpen(false)}
                  onOpenSettings={onOpenSettings}
                  onOpenContactForm={() => setShowContactForm(true)}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* お問い合わせモーダル（メニューから起動） */}
      {showContactForm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowContactForm(false)
            }
          }}
        >
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto relative p-6">
            <button
              onClick={() => setShowContactForm(false)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
              aria-label="閉じる"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pr-8">{t('contact.newContact')}</h2>
            <ContactForm
              onClose={() => setShowContactForm(false)}
              userName={user?.user_metadata?.nickname || user?.user_metadata?.full_name || ''}
              userEmail={user?.email || ''}
            />
          </div>
        </div>
      )}

      {/* プロジェクト共有モーダル */}
      {sharingProjectId && (
        <ShareProjectModal
          isOpen={true}
          onClose={() => setSharingProjectId(null)}
          projectId={sharingProjectId}
          projectName={projects.find(p => p.id === sharingProjectId)?.name || ''}
          currentTeamId={projects.find(p => p.id === sharingProjectId)?.team_id}
          onSuccess={async () => {
            // プロジェクト共有後、プロジェクト一覧とタスク一覧を再読み込み
            await useStore.getState().fetchProjects()
            // 現在選択中のプロジェクトのタスクも再読み込み（担当者・進捗クリアを反映）
            const { fetchTodos, selectedProjectId } = useStore.getState()
            await fetchTodos(selectedProjectId ?? undefined)
            setSharingProjectId(null)
          }}
        />
      )}

      {/* プロジェクト設定モーダル */}
      <ProjectSettingsModal
        isOpen={!!settingsProjectId}
        onClose={() => setSettingsProjectId(null)}
        project={settingsProject}
        milestones={localMilestones}
        templates={localMilestoneTemplates}
        onUpdateProject={(projectId, updates) => {
          updateProject(projectId, updates)
        }}
        onDeleteProject={(projectId) => {
          deleteProject(projectId)
          setSettingsProjectId(null)
        }}
        onAddMilestone={handleAddMilestone}
        onUpdateMilestone={handleUpdateMilestone}
        onDeleteMilestone={handleDeleteMilestone}
        onOpenTemplateManager={() => setTemplateManagerOpen(true)}
        onSyncTaskToGantt={() => {
          // モバイルでは簡略化：同期機能は省略
        }}
        onSyncGanttToTask={() => {
          // モバイルでは簡略化：同期機能は省略
        }}
        teamName={settingsProject?.team?.name}
        onTeamChange={async () => {
          // チーム変更後、プロジェクト一覧を再取得して表示を更新
          await useStore.getState().fetchProjects()
        }}
      />

      {/* マイルストーンテンプレート管理モーダル */}
      <MilestoneTemplateModal
        isOpen={templateManagerOpen}
        onClose={() => setTemplateManagerOpen(false)}
        templates={localMilestoneTemplates}
        teamName={settingsProject?.team?.name}
        isPersonal={!settingsProject?.team_id}
        onAdd={handleAddTemplate}
        onUpdate={handleUpdateTemplate}
        onDelete={handleDeleteTemplate}
      />
    </div>
  )
}
